import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import '@/App.css';
import LandingPage from '@/pages/LandingPage';
import Login from '@/pages/Login';
import CompleteProfile from '@/pages/CompleteProfile';
import Dashboard from '@/pages/Dashboard';
import Chat from '@/pages/Chat';
import Contacts from '@/pages/Contacts';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import InvitePage from '@/pages/InvitePage';
import InviteFriend from '@/pages/InviteFriend';
import DebugLogs from '@/pages/DebugLogs';
// Legal Pages
import TermsConditions from '@/pages/TermsConditions';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import AcceptableUse from '@/pages/AcceptableUse';
import CookiePolicy from '@/pages/CookiePolicy';
import LawEnforcement from '@/pages/LawEnforcement';
import DataDeletion from '@/pages/DataDeletion';
import Disclaimer from '@/pages/Disclaimer';
// AEO Pages
import AnonymousChat from '@/pages/AnonymousChat';
import SendMessageWithoutPhone from '@/pages/SendMessageWithoutPhone';
import PrivateMessagingGuide from '@/pages/PrivateMessagingGuide';
import { Toaster } from 'sonner';
import { initLogger } from '@/utils/logger';
import { trackPageView, setClarityUser } from '@/utils/analytics';

// Route change tracker for SPA analytics
function RouteTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Get a readable page name for Clarity heatmaps
    const getPageName = (pathname) => {
      const routes = {
        '/': 'Landing Page',
        '/login': 'Login',
        '/dashboard': 'Dashboard',
        '/contacts': 'Contacts',
        '/admin': 'Admin Login',
        '/admin/dashboard': 'Admin Dashboard',
        '/terms': 'Terms & Conditions',
        '/privacy': 'Privacy Policy',
        '/acceptable-use': 'Acceptable Use',
        '/cookies': 'Cookie Policy',
        '/law-enforcement': 'Law Enforcement',
        '/data-deletion': 'Data Deletion',
        '/disclaimer': 'Disclaimer',
        '/complete-profile': 'Complete Profile',
        '/invite-friend': 'Invite Friend',
        '/debug-logs': 'Debug Logs',
      };
      
      // Check exact match first
      if (routes[pathname]) return routes[pathname];
      
      // Check pattern matches
      if (pathname.startsWith('/chat/')) return 'Chat';
      if (pathname.startsWith('/invite/') || pathname.startsWith('/j/')) return 'Invite Page';
      
      return pathname;
    };
    
    const pageName = getPageName(location.pathname);
    
    // Track page view on route change with readable name
    trackPageView(location.pathname, pageName);
    
    // Set Clarity user if logged in
    const userId = localStorage.getItem('userId');
    if (userId) {
      setClarityUser(userId);
    }
  }, [location]);
  
  return null;
}

function PrivateRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const adminToken = localStorage.getItem('adminToken');
  return adminToken ? children : <Navigate to="/admin" />;
}

function App() {
  // Initialize debug logger on app start
  useEffect(() => {
    initLogger();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <RouteTracker />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/invite/:token" element={<InvitePage />} />
          <Route path="/invite" element={<InvitePage />} />
          <Route path="/j/:username/:code" element={<InvitePage />} />
          <Route path="/complete-profile" element={<PrivateRoute><CompleteProfile /></PrivateRoute>} />
          <Route path="/invite-friend" element={<PrivateRoute><InviteFriend /></PrivateRoute>} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/contacts" element={<PrivateRoute><Contacts /></PrivateRoute>} />
          <Route path="/chat/:conversationId" element={<PrivateRoute><Chat /></PrivateRoute>} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/debug-logs" element={<DebugLogs />} />
          {/* Legal Pages */}
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/acceptable-use" element={<AcceptableUse />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/law-enforcement" element={<LawEnforcement />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          {/* AEO Pages */}
          <Route path="/anonymous-chat" element={<AnonymousChat />} />
          <Route path="/send-message-without-phone-number" element={<SendMessageWithoutPhone />} />
          <Route path="/private-messaging-guide" element={<PrivateMessagingGuide />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
